import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  Tooltip,
  Divider,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import PrintIcon from "@material-ui/icons/Print";
import ApiClient from "utils/ApiClient";

// ─── Color Palette ────────────────────────────────────────────────────────────
const C = {
  boilerBg: "#ffffff",
  boilerAccent: "#e3eaf2",
  g1Bg: "#607d8b",
  g2Bg: "#1976d2",
  g2Light: "#e8f0fe",
  g2Mid: "#bbdefb",
  rpBin: "#00897b",
  rpBinLight: "#e0f2f1",
  cpBin: "#ef8c00",
  cpBinLight: "#fff8e1",
  processBin: "#e53935",
  processBinLight: "#ffebee",
  finalBin: "#7b1fa2",
  finalBinLight: "#f3e5f5",
  inUse: "#d32f2f",
  available: "#2e7d32",
  inUseBg: "#ffebee",
  availableBg: "#e8f5e9",
  empty: "#90a4ae",
  emptyBg: "#f5f7f9",
  cardBorder: "#e0e0e0",
  sectionBg: "#fff",
  dialogBg: "#f4f6fb",
  trapBg: "#dce5ec",
  trapBorder: "#b0bec5",
};

// ─── Typography helpers ───────────────────────────────────────────────────────
const font = (size, weight = 400, color = "#263238", extra = {}) => ({
  fontSize: size,
  fontWeight: weight,
  color,
  fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
  ...extra,
});

// ─── Date formatter ───────────────────────────────────────────────────────────
function fmtDate(d) {
  const date = d ? new Date(d) : new Date();
  if (isNaN(date)) return "";
  return date.toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════════════════════

// ── Boiler metric row ─────────────────────────────────────────────────────────
function MetricRow({ label, value, is24h }) {
  const empty = value === "" || value === null || value === undefined;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "5px 14px",
        borderBottom: "1px solid #f0f4f8",
      }}
    >
      <span
        style={{
          ...font(10, 500, is24h ? "#e57373" : "#78909c", {
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }),
        }}
      >
        {label}
      </span>
      <span
        style={{
          ...font(15, 700, is24h ? "#e53935" : "#1565c0", {
            fontFamily: "'Courier New', Courier, monospace",
            minWidth: 60,
            textAlign: "right",
          }),
        }}
      >
        {empty ? "—" : value}
      </span>
    </div>
  );
}

// ── Section label chip ────────────────────────────────────────────────────────
function SectionChip({ label, color, bg }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: bg || color,
        borderRadius: 4,
        padding: "3px 10px",
        marginBottom: 7,
      }}
    >
      <span style={font(10, 700, "#fff", { letterSpacing: 0.5, textTransform: "uppercase" })}>
        {label}
      </span>
    </div>
  );
}

// ── Status pill ───────────────────────────────────────────────────────────────
function StatusPill({ status, color }) {
  const inUse = status === "In Use";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: 20,
        backgroundColor: color || (inUse ? C.inUseBg : C.availableBg),
        border: `1px solid ${inUse ? C.inUse : C.available}`,
        ...font(9, 700, inUse ? C.inUse : C.available, { letterSpacing: 0.3 }),
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          backgroundColor: inUse ? C.inUse : C.available,
          marginRight: 4,
          display: "inline-block",
        }}
      />
      {inUse ? "In Use" : "Available"}
    </span>
  );
}

// ── Bin card ──────────────────────────────────────────────────────────────────
function BinCard({ binName, binData, accentColor, lightBg, compact }) {
  const batch = binData?.column_373 || "";
  const variety = binData?.column_489 || "";
  const status = binData?.column_372 || "";
  const statusColor = binData?.column_372_color;
  const hasBatch = batch || variety;

  return (
    <div
      style={{
        background: hasBatch ? lightBg || "#fff" : "#fafafa",
        border: `1.5px solid ${hasBatch ? accentColor || C.cardBorder : C.cardBorder}`,
        borderRadius: 7,
        padding: compact ? "6px 8px" : "8px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        minHeight: compact ? 68 : 82,
        boxShadow: hasBatch
          ? `0 2px 8px ${accentColor}22`
          : "0 1px 3px rgba(0,0,0,0.05)",
        transition: "box-shadow 0.2s",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* accent top bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: accentColor || C.cardBorder,
          borderRadius: "6px 6px 0 0",
          opacity: hasBatch ? 1 : 0.3,
        }}
      />
      <span style={font(10, 700, "#455a64", { textTransform: "uppercase", letterSpacing: 0.4, marginTop: 3 })}>
        {binName}
      </span>
      {hasBatch ? (
        <>
          {batch && (
            <span style={font(10, 500, "#546e7a", { lineHeight: 1.4, wordBreak: "break-word" })}>
              {batch}
            </span>
          )}
          {variety && (
            <span style={font(12, 700, "#1a237e")}>
              {variety}
            </span>
          )}
        </>
      ) : (
        <span style={font(12, 300, C.empty, { textAlign: "center", margin: "auto 0" })}>
          —
        </span>
      )}
      {status && (
        <div style={{ marginTop: "auto", paddingTop: 4 }}>
          <StatusPill status={status} />
        </div>
      )}
    </div>
  );
}

// ── Trapezoid cell (G1) ───────────────────────────────────────────────────────
function TrapCell({ label }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      <span style={font(9, 600, "#607d8b", { textTransform: "uppercase", letterSpacing: 0.4 })}>
        {label}
      </span>
      <div
        style={{
          width: "100%",
          height: 54,
          background: `linear-gradient(180deg, #eceff1 0%, ${C.trapBg} 100%)`,
          clipPath: "polygon(12% 0%, 88% 0%, 76% 100%, 24% 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span style={font(14, 300, "#90a4ae")}>—</span>
      </div>
    </div>
  );
}

// ── Empty card for G1 dryers / placeholder items ──────────────────────────────
function EmptyCard({ label, italic }) {
  return (
    <div
      style={{
        background: "#f5f7f9",
        border: `1.5px dashed ${C.cardBorder}`,
        borderRadius: 7,
        padding: "8px 10px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 70,
        gap: 4,
      }}
    >
      <span style={font(10, 600, "#78909c", { textTransform: "uppercase", letterSpacing: 0.4, textAlign: "center" })}>
        {label}
      </span>
      <span style={font(14, 300, "#b0bec5")}>—</span>
    </div>
  );
}

// ── G1 dryer pill (colored text) ──────────────────────────────────────────────
function G1DryerCard({ label, color }) {
  return (
    <div
      style={{
        background: "#f5f7f9",
        border: `1.5px solid ${color}44`,
        borderRadius: 7,
        padding: "8px 10px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 62,
        gap: 3,
      }}
    >
      <span style={font(11, 700, color, { letterSpacing: 0.3 })}>{label}</span>
      <span style={font(14, 300, "#b0bec5")}>—</span>
    </div>
  );
}

// ── Godown item (compact horizontal) ─────────────────────────────────────────
function GodownItem({ n }) {
  return (
    <div
      style={{
        background: "#f5f7f9",
        border: `1px solid ${C.cardBorder}`,
        borderRadius: 6,
        padding: "5px 10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: 36,
      }}
    >
      <span style={font(10, 600, "#78909c", { textTransform: "uppercase", letterSpacing: 0.3 })}>
        Godown {n}
      </span>
      <span style={font(12, 300, "#b0bec5")}>—</span>
    </div>
  );
}

// ── Section block title (inside APIT) ─────────────────────────────────────────
function BlockTitle({ label, color }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 7,
        marginTop: 4,
      }}
    >
      <div style={{ width: 4, height: 16, background: color, borderRadius: 2 }} />
      <span style={font(11, 700, color, { letterSpacing: 0.4, textTransform: "uppercase" })}>
        {label}
      </span>
      <div style={{ flex: 1, height: 1, background: `${color}30` }} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════════
function DPARHarvestProcessBinReport({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [boilerData, setBoilerData] = useState(null);
  const [binMap, setBinMap] = useState({});
  const [reportDate, setReportDate] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [boilerResp, binResp] = await Promise.all([
        ApiClient.Views.getViewData({
          list_id: 81545463,
          view_id: 32,
          criteria: "DATE( BoilerTurbineLog.date ) = CURDATE()",
          limit: 1,
        }),
        ApiClient.Views.getViewData({
          list_id: 85116746,
          view_id: 100,
          limit: 1000,
        }),
      ]);

      if (boilerResp?.success && boilerResp.data?.data?.length > 0) {
        const row = boilerResp.data.data[0];
        setBoilerData(row);
        setReportDate(fmtDate(row.column_122));
      } else {
        setBoilerData(null);
        setReportDate(fmtDate(null));
      }

      const map = {};
      if (binResp?.success && binResp.data?.data?.length > 0) {
        binResp.data.data.forEach((row) => {
          map[(row.column_371 || "").trim().toLowerCase()] = row;
        });
      }
      setBinMap(map);
    } catch (err) {
      console.error("DPAR fetch error:", err);
      setError("Failed to load report data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  const getBin = (name) => binMap[(name || "").trim().toLowerCase()] || null;
  const bt = boilerData || {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        style: {
          background: "#f4f6fb",
          borderRadius: 12,
          maxHeight: "96vh",
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        },
      }}
    >
      {/* Print style overrides */}
      <style>{`
        @media print {
          .dpar-no-print { display: none !important; }
          .MuiDialog-root .MuiBackdrop-root { display: none !important; }
          .MuiDialog-paper {
            box-shadow: none !important;
            border-radius: 0 !important;
            max-height: none !important;
            margin: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          .MuiDialogContent-root { overflow: visible !important; }
        }
      `}</style>
      {/* ── Title Bar ── */}
      <div
        style={{
          background: "linear-gradient(135deg, #1565c0 0%, #1976d2 60%, #2196f3 100%)",
          padding: "14px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: "12px 12px 0 0",
          boxShadow: "0 2px 8px rgba(21,101,192,0.25)",
        }}
      >
        <div>
          <div
            style={{
              ...font(20, 800, "#fff", {
                letterSpacing: 0.5,
                textShadow: "0 1px 3px rgba(0,0,0,0.2)",
              }),
            }}
          >
            Daily Production Activity Report{" "}
            <span style={{ color: "#90caf9" }}>[DPAR]</span>
          </div>
          <div style={font(12, 400, "rgba(255,255,255,0.75)", { marginTop: 3 })}>
            MS Agro Industries&nbsp;&nbsp;|&nbsp;&nbsp;
            {reportDate || fmtDate(null)}
          </div>
        </div>
        <div className="dpar-no-print" style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Tooltip title="Refresh">
            <IconButton size="small" style={{ color: "rgba(255,255,255,0.85)" }} onClick={fetchData} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print">
            <IconButton size="small" style={{ color: "rgba(255,255,255,0.85)" }} onClick={() => window.print()}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" style={{ color: "rgba(255,255,255,0.85)" }} onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* ── Body ── */}
      <DialogContent style={{ padding: 14, overflow: "auto" }}>
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 320, gap: 16 }}>
            <CircularProgress style={{ color: "#1565c0" }} />
            <span style={font(13, 400, "#607d8b")}>Loading production data…</span>
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: 40, ...font(14, 500, C.inUse) }}>
            {error}
          </div>
        ) : (
          /* ── 3-column master grid ── */
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "210px 220px 1fr",
              gap: 12,
              minWidth: 980,
            }}
          >
            {/* ╔═══════════════════════════════╗
                ║  COL 1: Boiler & Turbine      ║
                ╚═══════════════════════════════╝ */}
            <div
              style={{
                background: "#ffffff",
                borderRadius: 10,
                overflow: "hidden",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                border: "1px solid #e3eaf2",
              }}
            >
              <div
                style={{
                  background: "linear-gradient(90deg, #e8edf5, #dce6f5)",
                  padding: "10px 14px",
                  borderBottom: "2px solid #b0c4de",
                }}
              >
                <span style={font(12, 800, "#2c4a6e", { letterSpacing: 1, textTransform: "uppercase" })}>
                  Boiler &amp; Turbine
                </span>
              </div>
              <div style={{ paddingBottom: 8 }}>
                <MetricRow label="Steam Flow" value={bt.column_123} />
                <MetricRow label="Steam Total" value={bt.column_124} />
                <MetricRow label="Steam 24H" value={bt.column_125} is24h />
                <MetricRow label="Blowdown Total" value={bt.column_126} />
                <MetricRow label="Blowdown 24H" value={bt.column_127} is24h />
                <MetricRow label="Feed Water Temp" value={bt.column_128} />
                <MetricRow label="CRS Total" value={bt.column_129} />
                <MetricRow label="CRS 24H" value={bt.column_130} is24h />
                <div style={{ height: 1, background: "rgba(255,255,255,0.1)", margin: "6px 14px" }} />
                <MetricRow label="Turbine Export" value={bt.column_131} />
                <MetricRow label="Turbine Export 24H" value={bt.column_132} is24h />
                <MetricRow label="Turbine Import" value={bt.column_133} />
                <MetricRow label="Turbine Import 24H" value={bt.column_134} is24h />
                <MetricRow label="Turbine Running" value={bt.column_135} />
                {bt.column_136 ? (
                  <div style={{ padding: "8px 14px", borderTop: "1px solid #f0f4f8" }}>
                    <span style={font(9, 600, "#78909c", { textTransform: "uppercase", letterSpacing: 0.4 })}>Remarks</span>
                    <div style={font(11, 400, "#546e7a", { marginTop: 3, fontStyle: "italic" })}>{bt.column_136}</div>
                  </div>
                ) : null}
              </div>
            </div>

            {/* ╔═══════════════════════════════╗
                ║  COL 2: Old Dryer Section/G1  ║
                ╚═══════════════════════════════╝ */}
            <div
              style={{
                background: "#ffffff",
                borderRadius: 10,
                overflow: "hidden",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                border: "1px solid #e3eaf2",
              }}
            >
              <div
                style={{
                  background: "linear-gradient(90deg, #eceff1, #e0e7ee)",
                  padding: "10px 14px",
                  borderBottom: "2px solid #b0bec5",
                }}
              >
                <span style={font(12, 800, "#37474f", { letterSpacing: 1, textTransform: "uppercase" })}>
                  Old Dryer Section
                </span>
              </div>
              <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
                {/* G1 label */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: "#eceff1",
                    borderRadius: 6,
                    padding: "5px 10px",
                  }}
                >
                  <span style={font(18, 900, "#455a64", { letterSpacing: 2 })}>G1</span>
                  <div style={{ flex: 1, height: 1.5, background: "#b0bec5", borderRadius: 1 }} />
                </div>

                {/* Cutting Bunker | Steam Tanks */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <TrapCell label="Cutting Bunker" />
                  <TrapCell label="Steam Tanks" />
                </div>

                {/* Old Dryer | New Dryer */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <G1DryerCard label="Old Dryer" color="#e53935" />
                  <G1DryerCard label="New Dryer" color="#2e7d32" />
                </div>

                {/* Milling Bunker A | B */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <TrapCell label="Milling Bunker A" />
                  <TrapCell label="Milling Bunker B" />
                </div>

                {/* Milling Bunker C | D */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <TrapCell label="Milling Bunker C" />
                  <TrapCell label="Milling Bunker D" />
                </div>
              </div>
            </div>

            {/* ╔═══════════════════════════════════════════════════════╗
                ║  COL 3: APIT Dryer Section (G2)                      ║
                ╚═══════════════════════════════════════════════════════╝ */}
            <div
              style={{
                background: "#ffffff",
                borderRadius: 10,
                overflow: "hidden",
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                border: "1px solid #e3eaf2",
              }}
            >
              {/* Section header */}
              <div
                style={{
                  background: "linear-gradient(90deg, #e3f0ff, #d6e8ff)",
                  padding: "10px 14px",
                  borderBottom: "2px solid #90c2f7",
                }}
              >
                <span style={font(12, 800, "#1565c0", { letterSpacing: 1, textTransform: "uppercase" })}>
                  APIT Dryer Section
                </span>
              </div>

              <div style={{ padding: "10px 12px" }}>
                {/* G2 header row */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: "linear-gradient(90deg, #f0f7ff, #eef5ff)",
                    borderRadius: 7,
                    padding: "8px 14px",
                    marginBottom: 12,
                    border: "1px solid #c8dff8",
                  }}
                >
                  <span style={font(20, 900, "#1565c0", { letterSpacing: 2 })}>G2</span>
                  <div style={{ width: 1, height: 28, background: "#a8c8f0" }} />
                  <div>
                    {getBin("harvest bin 1")?.column_373 ? (
                      <>
                        <div style={font(11, 600, "#1976d2")}>
                          {getBin("harvest bin 1")?.column_373}
                        </div>
                        <div style={font(12, 800, "#1565c0")}>
                          {getBin("harvest bin 1")?.column_489}
                        </div>
                      </>
                    ) : (
                      <span style={font(12, 400, "#90a4ae", { fontStyle: "italic" })}>
                        No batch assigned
                      </span>
                    )}
                  </div>
                </div>

                {/* ── Inner 2-column layout ──────────────────────────────────
                    Left:  Harvest Bins 1-4, Harvest Dryer Bins 1-2, Godowns 1-4
                    Right: Process Bins 5-12, Dryers/Cooker, Final Bins 13-20
                ─────────────────────────────────────────────────────────── */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1.7fr",
                    gap: 12,
                    alignItems: "start",
                  }}
                >
                  {/* ── LEFT inner column ─────────────────────────────── */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {/* Harvest Bins 1-4 */}
                    <div>
                      <BlockTitle label="Harvest Bins" color={C.rpBin} />
                      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                        {["harvest bin 1", "harvest bin 2", "harvest bin 3", "harvest bin 4"].map(
                          (key, i) => (
                            <BinCard
                              key={key}
                              binName={`Harvest Bin ${i + 1}`}
                              binData={getBin(key)}
                              accentColor={C.rpBin}
                              lightBg={C.rpBinLight}
                            />
                          )
                        )}
                      </div>
                    </div>

                    {/* Harvest Dryer Bins 1-2 */}
                    <div>
                      <BlockTitle label="Harvest Dryer Bins" color={C.cpBin} />
                      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                        {["harvest dryer 1", "harvest dryer 2"].map((key, i) => (
                          <BinCard
                            key={key}
                            binName={`Harvest Dryer Bin ${i + 1}`}
                            binData={getBin(key)}
                            accentColor={C.cpBin}
                            lightBg={C.cpBinLight}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Godowns 1-4 */}
                    <div>
                      <BlockTitle label="Godowns" color="#607d8b" />
                      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        {[1, 2, 3, 4].map((n) => (
                          <GodownItem key={n} n={n} />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ── RIGHT inner column ────────────────────────────── */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {/* Process Bins 5-12 */}
                    <div>
                      <BlockTitle label="Process Bins" color={C.processBin} />
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 1fr 1fr",
                          gap: 7,
                        }}
                      >
                        {[5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                          <BinCard
                            key={n}
                            binName={`Process Bin ${n}`}
                            binData={getBin(`process bin ${n}`)}
                            accentColor={C.processBin}
                            lightBg={C.processBinLight}
                            compact
                          />
                        ))}
                      </div>
                    </div>

                    {/* Process Dryer 1 | Cooker | Process Dryer 2 */}
                    <div>
                      <BlockTitle label="Dryers &amp; Cooker" color="#455a64" />
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 1fr",
                          gap: 7,
                        }}
                      >
                        <EmptyCard label="Process Dryer 1" />
                        <EmptyCard label="Cooker" />
                        <EmptyCard label="Process Dryer 2" />
                      </div>
                    </div>

                    {/* Final Bins 13-20 */}
                    <div>
                      <BlockTitle label="Final Bins" color={C.finalBin} />
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 1fr 1fr",
                          gap: 7,
                        }}
                      >
                        {[13, 14, 15, 16, 17, 18, 19, 20].map((n) => (
                          <BinCard
                            key={n}
                            binName={`Final Bin ${n}`}
                            binData={getBin(`final bin ${n}`)}
                            accentColor={C.finalBin}
                            lightBg={C.finalBinLight}
                            compact
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* end col 3 */}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DPARHarvestProcessBinReport;
